import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { Box } from '../../../../../../../../CommonComponents/OPL/Box/Box';
import { Label } from '../../../../../../../../CommonComponents/OPL/Label/Label';
import { Select } from '../../../../../../../../CommonComponents/OPL/Select/Select/Select';
import { Toggle } from '../../../../../../../../CommonComponents/OPL/Toggle/Toggle';
import { DistributionStrategyTypeDTO } from '../../../../../../api/useAccountsOptionsQuery';
import { InputNumberMask } from '../../../../../../../../CommonComponents/OPL/Input/InputNumberMask';
import {
  END_AT_RETIREMENT,
  END_OF_PLAN,
  START_AT_RETIREMENT,
  START_TODAY,
} from './DistributionStrategy.meta';
import { useAccountFormContext } from '../../context/useAccountFormContext';
import { FormFields } from '../../AccountForm.meta';
import { useYearAgeOptionsWithRightLabel } from '../ContributionStartYearSelect/useYearAgeOptionsWithRightLabel';

export const DistributionStrategySelect = () => {
  const {
    options: { CustomDistributionStrategyOptions },
  } = useAccountFormContext();

  const formBag = useFormContext<FormFields>();

  const { control, watch } = formBag;

  const [
    distributionStrategyType,
    distributionStrategy,
    startAtRetirement,
    startToday,
    startAfterYears,
    endAtRetirement,
    endOfPlan,
    endAfterYears,
  ] = watch([
    'bankAccount.DistributionStrategyType',
    'bankAccount.DistributionStrategy',
    'bankAccount.DistributionStrategy.StartDistributionsAtRetirement',
    'bankAccount.DistributionStrategy.StartDistributionsToday',
    'bankAccount.DistributionStrategy.StartAfterYears',
    'bankAccount.DistributionStrategy.EndDistributionsAtRetirement',
    'bankAccount.DistributionStrategy.EndOfPlan',
    'bankAccount.DistributionStrategy.EndAfterYears',
  ]);

  const startYearOptions = useYearAgeOptionsWithRightLabel({
    options: CustomDistributionStrategyOptions.distributionStartYearType,
  });

  const endYearOptions = useYearAgeOptionsWithRightLabel({
    options: CustomDistributionStrategyOptions.distributionEndYearType,
  });

  return (
    <>
      <Controller
        control={control}
        name="bankAccount.DistributionStrategyType"
        render={({ field: { value, onChange, ...field } }) => {
          return (
            <Select
              {...field}
              isPortal
              value={CustomDistributionStrategyOptions.distributionStrategyType.find(
                option => option.value === value,
              )}
              options={CustomDistributionStrategyOptions.distributionStrategyType}
              onChange={opt => {
                onChange(opt?.value);
              }}
              label="Distribution Strategy"
              tooltip="By adding a Distribution Strategy, you will force distributions from this account regardless if the distribution is needed to fund expenses or goals."
            />
          );
        }}
      />

      {distributionStrategyType === DistributionStrategyTypeDTO.DollarAmount && (
        <Controller
          control={formBag.control}
          name="bankAccount.DistributionStrategy.DistributionDollarAmount"
          render={({ field }) => (
            <InputNumberMask
              {...field}
              leftIcon="fa fa-dollar-sign"
              label="Annual Distribution Amount"
              allowNegative={false}
              decimalScale={2}
            />
          )}
        />
      )}

      {distributionStrategyType === DistributionStrategyTypeDTO.PercentageOfAccountBalance && (
        <Controller
          control={formBag.control}
          name="bankAccount.DistributionStrategy.DistributionPercentage"
          render={({ field }) => (
            <InputNumberMask
              {...field}
              rightIcon="far fa-percent"
              decimalScale={2}
              allowNegative={false}
              thousandSeparator={false}
              label="Distribution Percentage"
              tooltip="This Percentage will be applied to the Account Balance based on the Computation Year selection."
            />
          )}
        />
      )}

      {distributionStrategyType === DistributionStrategyTypeDTO.PercentageOfAccountBalance && (
        <Controller
          control={control}
          name="bankAccount.DistributionStrategy.ComputationYearType" // CHECK THIS
          render={({ field: { value, onChange, ...field } }) => {
            return (
              <Select
                {...field}
                isPortal
                value={CustomDistributionStrategyOptions.computationYearType.find(
                  option => option.value === value,
                )}
                options={CustomDistributionStrategyOptions.computationYearType}
                onChange={opt => onChange(opt?.value)}
                label="Computation Year"
                tooltip="This selection will determine what account balance will be used when applying the Distribution Percentage."
              />
            );
          }}
        />
      )}

      {distributionStrategyType !== DistributionStrategyTypeDTO.None && (
        <>
          <Box
            css={`
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 16px;
            `}
          >
            <Controller
              control={control}
              name="bankAccount.CustomDistributionStrategy.DistributionStartYearType"
              render={() => {
                return (
                  <Select
                    isPortal
                    options={startYearOptions}
                    label="Distribution Start Year"
                    value={(() => {
                      if (startAtRetirement) {
                        return startYearOptions.find(({ value }) => value === START_AT_RETIREMENT);
                      }

                      if (startToday) {
                        return startYearOptions.find(({ value }) => value === START_TODAY);
                      }

                      if (typeof startAfterYears === 'number') {
                        return startYearOptions.find(
                          ({ value }) => value === startAfterYears.toString(),
                        );
                      }

                      return startYearOptions[0];
                    })()}
                    onChange={opt => {
                      if (opt?.value === START_AT_RETIREMENT) {
                        formBag.setValue('bankAccount.DistributionStrategy', {
                          ...distributionStrategy,
                          StartDistributionsAtRetirement: true,
                          StartDistributionsToday: false,
                          StartAfterYears: null,
                        });
                      } else if (opt?.value === START_TODAY) {
                        formBag.setValue('bankAccount.DistributionStrategy', {
                          ...distributionStrategy,
                          StartDistributionsAtRetirement: false,
                          StartDistributionsToday: true,
                          StartAfterYears: null,
                        });
                      } else {
                        formBag.setValue('bankAccount.DistributionStrategy', {
                          ...distributionStrategy,
                          StartDistributionsAtRetirement: false,
                          StartDistributionsToday: false,
                          StartAfterYears: opt ? Number(opt.value) : null,
                        });
                      }
                    }}
                  />
                );
              }}
            />

            <Controller
              control={control}
              name="bankAccount.CustomDistributionStrategy.DistributionEndYearType"
              render={() => {
                return (
                  <Select
                    isPortal
                    options={endYearOptions}
                    label="Distribution End Year"
                    value={(() => {
                      if (endAtRetirement) {
                        return endYearOptions.find(({ value }) => value === END_AT_RETIREMENT);
                      }

                      if (endOfPlan) {
                        return endYearOptions.find(({ value }) => value === END_OF_PLAN);
                      }

                      if (typeof endAfterYears === 'number') {
                        return endYearOptions.find(
                          ({ value }) => value === endAfterYears.toString(),
                        );
                      }

                      return endYearOptions[0];
                    })()}
                    onChange={opt => {
                      if (opt?.value === END_AT_RETIREMENT) {
                        formBag.setValue('bankAccount.DistributionStrategy', {
                          ...distributionStrategy,
                          EndDistributionsAtRetirement: true,
                          EndOfPlan: false,
                          EndAfterYears: null,
                        });
                      } else if (opt?.value === END_OF_PLAN) {
                        formBag.setValue('bankAccount.DistributionStrategy', {
                          ...distributionStrategy,
                          EndDistributionsAtRetirement: false,
                          EndOfPlan: true,
                          EndAfterYears: null,
                        });
                      } else {
                        formBag.setValue('bankAccount.DistributionStrategy', {
                          ...distributionStrategy,
                          EndDistributionsAtRetirement: false,
                          EndOfPlan: false,
                          EndAfterYears: opt ? Number(opt.value) : null,
                        });
                      }
                    }}
                  />
                );
              }}
            />
          </Box>

          <Box
            display="flex"
            flexDirection="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Box>
              <Label
                id="additional-distributions-allowed"
                tooltip="By selecting Yes, additional distributions can be made to help fund expenses/goals if there is a cash flow shortfall. By selecting No, the only distributions taken from this account will be the distributions indicated above even if there is a cash flow shortage."
              >
                Additional Distributions Allowed
              </Label>
            </Box>

            <Controller
              control={control}
              name="bankAccount.DistributionStrategy.NoOtherDistributionsAllowed"
              render={({ field: { value, onChange, ...field } }) => {
                return (
                  <Toggle
                    {...field}
                    checked={!value}
                    onChange={evt => onChange(!evt.target.checked)}
                    aria-labelledby="additional-distributions-allowed"
                  >
                    {' '}
                  </Toggle>
                );
              }}
            />
          </Box>
        </>
      )}
    </>
  );
};
